import { useState } from "react";
import { X, Package, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const printMethods = [
  { value: "screen-printing", label: "Screen Printing" },
  { value: "dtf-printing", label: "DTF Printing" },
  { value: "embroidery", label: "Embroidery" },
  { value: "not-sure", label: "Not Sure Yet" },
];

export default function SampleRequestModal({ product, onClose }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    preferred_size: "",
    print_method: "not-sure",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.preferred_size) {
      setError("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/sample-requests`, {
        ...form,
        garment_name: product.name,
        garment_sku: product.sku,
        garment_brand: product.brand,
      });
      setSuccess(true);
    } catch {
      setError("Something went wrong. Please try again or call us at (214) 555-0190.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="sample-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-[#111] border border-neutral-800 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sample-modal-content">
        {/* Header */}
        <div className="sticky top-0 bg-[#111] border-b border-neutral-800 px-6 py-4 flex items-start justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-red-600 flex items-center justify-center flex-shrink-0">
              <Package size={16} className="text-white" />
            </div>
            <div>
              <h2 className="font-teko text-xl text-white uppercase tracking-wide leading-none">Request Free Sample</h2>
              <p className="text-[10px] text-neutral-500 font-inter mt-0.5">We'll print a sample on your chosen garment</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="sample-modal-close" className="text-neutral-500 hover:text-white transition-colors p-1">
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="p-8 text-center" data-testid="sample-success">
            <div className="w-14 h-14 bg-green-900/30 border border-green-600/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={28} className="text-green-400" />
            </div>
            <h3 className="font-teko text-2xl text-white uppercase mb-2">Sample Request Received!</h3>
            <p className="text-neutral-400 font-inter text-sm mb-1">
              We'll prepare a printed sample of the <span className="text-white">{product.brand} {product.sku}</span> and contact you within 24 hours.
            </p>
            <p className="text-neutral-600 font-inter text-xs mb-6">Free sample — no obligation to order.</p>
            <button onClick={onClose} className="ninja-btn-primary font-teko tracking-widest px-8 py-3">CLOSE</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Garment Info */}
            <div className="bg-neutral-900/50 border border-neutral-800 p-3 flex items-center gap-3">
              <img
                src={product.image}
                alt={product.name}
                className="w-14 h-14 object-cover bg-neutral-800 flex-shrink-0"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1523381294911-8d3cead13475?w=100&q=60"; }}
              />
              <div>
                <div className="text-xs text-red-500 font-inter uppercase tracking-widest">{product.brand} {product.sku}</div>
                <div className="font-teko text-lg text-white uppercase leading-tight">{product.name}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-neutral-500 uppercase tracking-widest font-inter block mb-1">Full Name *</label>
                <input
                  data-testid="sample-name"
                  type="text"
                  value={form.full_name}
                  onChange={set("full_name")}
                  className="ninja-input w-full text-sm"
                  placeholder="John Smith"
                  required
                />
              </div>
              <div>
                <label className="text-[10px] text-neutral-500 uppercase tracking-widest font-inter block mb-1">Email *</label>
                <input
                  data-testid="sample-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className="ninja-input w-full text-sm"
                  placeholder="john@company.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-neutral-500 uppercase tracking-widest font-inter block mb-1">Phone *</label>
                <input
                  data-testid="sample-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  className="ninja-input w-full text-sm"
                  placeholder="(214) 555-0000"
                  required
                />
              </div>
              <div>
                <label className="text-[10px] text-neutral-500 uppercase tracking-widest font-inter block mb-1">Company</label>
                <input
                  data-testid="sample-company"
                  type="text"
                  value={form.company}
                  onChange={set("company")}
                  className="ninja-input w-full text-sm"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-neutral-500 uppercase tracking-widest font-inter block mb-1">Preferred Size *</label>
                <select
                  data-testid="sample-size"
                  value={form.preferred_size}
                  onChange={set("preferred_size")}
                  className="ninja-input w-full text-sm"
                  required
                >
                  <option value="">Select size</option>
                  {product.sizes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-neutral-500 uppercase tracking-widest font-inter block mb-1">Print Method</label>
                <select
                  data-testid="sample-method"
                  value={form.print_method}
                  onChange={set("print_method")}
                  className="ninja-input w-full text-sm"
                >
                  {printMethods.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-[10px] text-neutral-500 uppercase tracking-widest font-inter block mb-1">Notes / What to Print</label>
              <textarea
                data-testid="sample-notes"
                value={form.notes}
                onChange={set("notes")}
                className="ninja-input w-full text-sm"
                rows={2}
                placeholder="Tell us about your logo, colors, or what you'd like to test..."
              />
            </div>

            {error && (
              <div className="text-red-400 text-xs font-inter bg-red-900/20 border border-red-900/40 px-3 py-2" data-testid="sample-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="sample-submit"
              className="ninja-btn-primary w-full py-3.5 font-teko tracking-widest text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "SENDING..." : "REQUEST FREE SAMPLE"}
            </button>

            <p className="text-center text-[10px] text-neutral-600 font-inter">
              100% free — no obligation. We'll contact you within 24 hours to confirm details.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
